import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ReCAPTCHA from 'react-google-recaptcha'
import FormWrapper from '../FormWrapper/FormWrapper'
import Field from '../../atoms/fields/field'
import FormTitle from '../FormTitle/FormTitle'
import ValidationWrapper from '../../atoms/fields/ValidationWrapper'
import TextareaField from '../../atoms/fields/textarea'
import SetRatingComponent from '../SetRatingComponent/SetRatingComponent'
import ButtonAtom from '../../atoms/buttons/buttons'
import { useToast } from '../../../context/ToastContext'
import useCustomer from '../../../context/CustomerContext'
import validateCaptchaToken from '../../../utils/captcha/validateCaptchaToken'
import useFormValidation from '../../../hooks/useFormValidation'
import { reviewSchema } from '../../../utils/form/validationSchema'

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: wrap;
`

function AddReviewForm({ onSubmit, retingFromNotification }) {
  const [rating, setRating] = useState(retingFromNotification || 0)
  const [isLoading, setIsLoading] = useState(false)
  const recaptcha = useRef(null)
  const formRef = useRef(null)
  const { customer } = useCustomer()
  const { showToast } = useToast()
  const { validateValues, validateFieldOnChange, errors } = useFormValidation({
    schema: reviewSchema,
    formRef,
  })

  const { firstName, lastName, email: customerEmail } = customer
  const customerName = lastName ? `${firstName} ${lastName}` : firstName

  const handleErrors = async ({ target: { name, value } }) => {
    await validateFieldOnChange({ name, value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    const formData = new FormData(e.target)
    const formValues = {
      name: customerName || formData.get('name'),
      email: customerEmail || formData.get('email'),
      title: formData.get('title'),
      body: formData.get('body'),
      rating,
    }

    const validatedData = await validateValues(formValues)

    if (!validatedData) {
      showToast('Validation error', '')
      setIsLoading(false)
      return
    }

    const isCaptchaValid = await validateCaptchaToken(
      recaptcha.current.getValue(),
      showToast,
      setIsLoading
    )

    if (!isCaptchaValid) return

    try {
      await onSubmit(formValues)
    } catch (e) {
      setIsLoading(false)
    }

    setIsLoading(false)
    e.target.reset()
  }

  useEffect(() => {
    const handleRatingError = async () => {
      if (rating) await validateFieldOnChange({ name: 'rating', value: rating })
    }

    handleRatingError()
  }, [rating])

  return (
    <FormWrapper>
      <>
        <div className="margin-xxxxl">
          <FormTitle
            title="Leaving a review for this product"
            description="Thank you for leaving a review. Your feedback helps us continue to provide excellent service and informs other heliguy customers of your experience "
          />
        </div>
        <form ref={formRef} id="add-review" onSubmit={handleSubmit}>
          <div className={`${customerName ? 'margin-none' : 'margin-36'}`}>
            <ValidationWrapper errorMessage={errors.name}>
              <Field
                labelText="name"
                labelFor="name"
                placeholderText="Your name"
                fieldType={customerName ? 'hidden' : 'text'}
                fieldId="name"
                fieldName="name"
                onChange={handleErrors}
              />
            </ValidationWrapper>
          </div>
          <div className={`${customerEmail ? 'margin-none' : 'margin-36'}`}>
            <ValidationWrapper errorMessage={errors.email}>
              <Field
                labelText="email"
                labelFor="email"
                placeholderText="Your email"
                fieldType={customerEmail ? 'hidden' : 'email'}
                fieldId="email"
                fieldName="email"
                onChange={handleErrors}
              />
            </ValidationWrapper>
          </div>
          <div className="margin-36">
            <ValidationWrapper errorMessage={errors.title}>
              <Field
                labelText="title"
                labelFor="title"
                placeholderText="Your review title"
                fieldType="text"
                fieldId="title"
                fieldName="title"
                onChange={handleErrors}
              />
            </ValidationWrapper>
          </div>
          <div className="margin-36">
            <ValidationWrapper errorMessage={errors.body}>
              <TextareaField
                labelText="body"
                labelFor="body"
                placeholderText="Your review"
                fieldType="text"
                fieldId="body"
                fieldName="body"
                onChange={handleErrors}
              />
            </ValidationWrapper>
          </div>
        </form>
        <div className="margin-36">
          <ReCAPTCHA sitekey={process.env.GATSBY_SITE_KEY} ref={recaptcha} />
        </div>
        <FormFooter>
          <ValidationWrapper error={!!errors.rating} errorMessage={errors.rating}>
            <SetRatingComponent ratingValue={rating} onSetRating={setRating} />
          </ValidationWrapper>

          <ButtonAtom
            buttonType="Primary Button"
            buttonCopy="Submit review"
            form="add-review"
            type="submit"
            isLoading={isLoading}
          />
        </FormFooter>
      </>
    </FormWrapper>
  )
}

export default AddReviewForm
